import { Alert, Box, Divider, Stack, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { formatStoreSelectorLabel } from "@/utils/store";

interface StoreLabelPreviewSource {
  id: number | string;
  name: string | null | undefined;
  externalId?: string | null | undefined;
  customerId?: number | null | undefined;
  customerInitial?: string | null | undefined;
}

interface StoreLabelPreviewProps {
  alignment?: "center" | "start";
  store: StoreLabelPreviewSource;
}

const labelSx = {
  color: "text.secondary",
  fontSize: "0.75rem",
  fontWeight: 700,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
} as const;

/** Shows the two role-specific store labels used in selectors and filters. */
export const StoreLabelPreview: React.FC<StoreLabelPreviewProps> = ({
  alignment = "center",
  store,
}) => {
  const { t } = useTranslation();
  const hasPreview = Boolean(store.name?.trim() && store.externalId?.trim());
  const previewValue = (role: "CRAVE_ADMIN" | "CUSTOMER_ADMIN") =>
    hasPreview ? (
      formatStoreSelectorLabel(store, t, role)
    ) : (
      <Typography color="action.disabled" component="span">
        {t("management.devices.devices.empty.notAvailable")}
      </Typography>
    );

  return (
    <Stack spacing={3}>
      <Divider />
      <Alert severity="info">{t("management.stores.addStore.previews.description")}</Alert>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          justifyContent: alignment,
        }}
      >
        {[
          {
            label: t("management.stores.addStore.previews.craveView"),
            value: previewValue("CRAVE_ADMIN"),
          },
          {
            label: t("management.stores.addStore.previews.customerView"),
            value: previewValue("CUSTOMER_ADMIN"),
          },
        ].map(({ label, value }) => (
          <Stack
            key={label}
            spacing={0.25}
            sx={{ alignItems: alignment, minWidth: 0, textAlign: alignment }}
          >
            <Typography variant="body2" sx={labelSx}>
              {label}
            </Typography>
            <Typography variant="body1" sx={{ overflowWrap: "anywhere" }}>
              {value}
            </Typography>
          </Stack>
        ))}
      </Box>
    </Stack>
  );
};
